// HomePage control-handlers — F-H1 audit cycle #14 + R&D cycle #15 timed force.
//
// Strategy + Force-actie tiles waren STUBS (console.log) → operator klikte
// en dacht actie was uitgevoerd. Deze module wrappert de bestaande
// /api/admin/override-bulk endpoint (Strategy) en het nieuwe
// /api/controls/force/timed endpoint (Force-actie met auto-revert).
//
// Whitelist constraints (zie zbs_ems_monitor/api.py):
// - ems.strategy: {manual, revenue_max, self_use, peak_shaving, zero_export}
// - ems.manual.fixed_setpoint_kw: range -42..+42 kW. Sign:
//   + = discharge (battery → grid export)
//   - = charge (grid import → battery)
//
// Strategy-switch: trigger_restart=true zodat de loop de nieuwe config picked up
// zonder dat de operator nog een extra knop moet drukken. ~3s downtime van zbs-loop.
//
// Force-actie (R&D cycle #15): nieuw endpoint POST /api/controls/force/timed
// schrijft strategy=manual + setpoint + DB-rij in `force_actions`. Engine-tick
// revertert automatisch na `duration_hours`. Operator klikt eenmalig, geen
// handmatige stop meer nodig.

import { submitOverrideBulk } from './adminOverride';

export type Strategy = 'manual' | 'revenue_max' | 'self_use' | 'peak_shaving' | 'zero_export';
export type ForceDirection = 'charge' | 'discharge';
export type RevertStrategy = 'revenue_max' | 'self_use' | 'peak_shaving' | 'zero_export';

const TIMED_PATH = '/api/controls/force/timed';

/** Geserialiseerde DB-rij die het backend teruggeeft voor één force-actie. */
export interface TimedForceAction {
  id: number;
  started_at_utc: string;
  expires_at_utc: string;
  action: ForceDirection;
  power_kw: number;
  revert_to_strategy: string;
  status: 'active' | 'reverted' | 'cancelled' | 'expired';
  actor: string;
  /** Alleen aanwezig op GET (handler verrijkt met live-countdown). */
  countdown_seconds?: number;
  /** Gevuld na revert/cancel. */
  reverted_at_utc?: string | null;
}

/**
 * Switcht EMS-strategie en herstart de loop.
 *
 * Backend whitelist accepteert: manual, revenue_max, self_use, peak_shaving,
 * zero_export. Buiten die set → 400.
 */
export async function postStrategy(strategy: Strategy): Promise<void> {
  await submitOverrideBulk({
    changes: [{ key: 'ems.strategy', value: strategy }],
    trigger_restart: true,
  });
}

/**
 * Start een TIMED force-actie met auto-revert na `durationHours`.
 *
 * Body naar backend:
 * ```
 * {
 *   action: "charge" | "discharge",
 *   power_kw: <abs kW>,
 *   duration_hours: <number>,
 *   revert_to?: "revenue_max" | "self_use" | "peak_shaving" | "zero_export"
 * }
 * ```
 *
 * Backend schrijft override + DB-rij + triggert restart. Bij expiry: engine
 * reverteert automatisch (geen UI-actie nodig).
 *
 * Sign-conventie blijft hetzelfde als voorheen — backend bouwt 'm zelf op
 * basis van `action`, dus we hoeven hier alleen de absolute kW mee te geven.
 */
export async function startTimedForceAction(
  direction: ForceDirection,
  kw: number,
  durationHours: number,
  revertTo?: RevertStrategy,
): Promise<TimedForceAction> {
  const body: Record<string, unknown> = {
    action: direction,
    power_kw: Math.abs(kw),
    duration_hours: durationHours,
  };
  if (revertTo) body.revert_to = revertTo;

  const res = await fetch(TIMED_PATH, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let detail = '';
    try {
      const j = await res.json() as { detail?: string };
      detail = j.detail ?? '';
    } catch {
      try { detail = await res.text(); } catch { /* ignore */ }
    }
    throw new Error(`POST ${TIMED_PATH}: ${res.status} ${detail.slice(0, 200)}`);
  }
  const data = await res.json() as { id: number; force_action: TimedForceAction };
  return data.force_action;
}

/** Lijst actieve force-actions met countdown. Cookie OF anoniem (GET is public). */
export async function listTimedForceActions(): Promise<TimedForceAction[]> {
  const res = await fetch(TIMED_PATH, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) {
    let body = '';
    try { body = await res.text(); } catch { /* ignore */ }
    throw new Error(`GET ${TIMED_PATH}: ${res.status} ${body.slice(0, 200)}`);
  }
  const data = await res.json() as { active: TimedForceAction[] };
  return Array.isArray(data.active) ? data.active : [];
}

/** Cancel een actieve force-actie + immediate revert. Idempotent op verlopen ids. */
export async function cancelTimedForceAction(id: number): Promise<void> {
  const res = await fetch(`${TIMED_PATH}/${encodeURIComponent(String(id))}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) {
    let body = '';
    try { body = await res.text(); } catch { /* ignore */ }
    throw new Error(`DELETE ${TIMED_PATH}/${id}: ${res.status} ${body.slice(0, 200)}`);
  }
}
